import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { H2 } from '@hobs/components/Headings';
import { Download } from '@hobs/utils/download';
import Toast from '@hobs/components/Toast';
import Panel from '@hobs/components/Panel';
import Icon from '@hobs/components/Icon';
import InputLabel from '@hobs/components/InputLabel';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import PageActions from '@hobs/components/PageActions';
import WithEscNavigation from '@hobs/hocs/WithEscNavigation';
import { format, parseLocal } from '@hobs/utils/formatWithTimeZone';
import {
  DATE_FORMAT,
  USER_ROLES,
  BACKEND_DATE_FORMAT,
  PARTY_TYPE
} from '@hobs/utils/constants';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import Switch from '@hobs/components/Switch';
import Money from '@hobs/components/Money';
import Input from '@hobs/components/Input';
import File from '@hobs/components/File';
import Confirm from '@hobs/components/Confirm';
import DateInput from '@hobs/components/DateInput';
import Textarea from '@hobs/components/Textarea';
import DropZone from '@hobs/components/DropZone';
import PageHeader from '@hobs/components/PageHeader';

import './Order.scss';
import Quantity from '@hobs/components/Quantity';

const SUBMIT_TYPES = {
  SAVE_DRAFT: 'SAVE_DRAFT',
  SAVE_ORDER: 'SAVE_ORDER',
  CANCEL_ORDER: 'CANCEL_ORDER',
  UPDATE_AND_SEND: 'UPDATE_AND_SEND'
};

class Order extends PureComponent {
  constructor(props) {
    super(props);

    const { t } = this.props;

    this.form = React.createRef();
    this.validationSchema = Yup.object().shape({
      subcontractorInformation: Yup.object().shape({
        email: Yup.string()
          .email(t('Invalid email format'))
          .max(128, t('Email cannot be longer than 128 symbols')),
        address: Yup.string().max(
          128,
          t('Address cannot be longer than 128 symbols')
        ),
        phone: Yup.string().max(
          128,
          t('Phone cannot be longer than 128 symbols')
        )
      }),
      deliveryNotes: Yup.string()
        .nullable()
        .max(500, t('Notes cannot be longer than 500 symbols')),
      orderInformation: Yup.object().shape({
        notes: Yup.string()
          .nullable()
          .max(500, t('Notes cannot be longer than 500 symbols')),
        responseDeadline: Yup.string()
          .nullable()
          .required(t('Response deadline is required'))
      })
    });

    this.state = {
      preview: true,
      deletedServiceRequests: []
    };
  }

  componentWillUnmount() {
    this.props.clearAttachments();
  }

  async componentDidMount() {
    await this.props.getOrder(this.props.match.params.orderId);
    this.props.setAttachments(this.props.order.attachments);
    await this.props.getCurrentParty();
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  getFieldValue(value) {
    return value && value !== '' ? value : '-';
  }

  isPreviewMode() {
    const { order, hasUserPermission } = this.props;

    return (
      (!order.isDraft && this.state.preview) ||
      hasUserPermission(USER_ROLES.READONLY)
    );
  }

  isPreviewStateMode() {
    return false;
  }

  isOrderDraft() {
    return this.props.order.isDraft;
  }

  getInitialValues() {
    const { order } = this.props;

    if (this.isPreviewMode()) {
      return {
        includeOrderedServices: true,
        ...order
      };
    }

    return {
      includeOrderedServices: true,
      ...order,
      orderInformation: {
        ...order.orderInformation,
        creationDate: order.orderInformation.creationDate,
        responseDeadline: order.orderInformation.responseDeadline
      }
    };
  }

  onSubmit = (values, actions) => {
    const {
      t,
      sendOrder,
      cancelOrder,
      history,
      getOrder,
      updateOrder,
      updateAndSend,
      match: {
        params: { orderId }
      }
    } = this.props;

    // TODO: Possibly we need to fix it globally ð¢
    const responseDeadline =
      parseLocal(values.orderInformation.responseDeadline).toString() !==
      'Invalid Date'
        ? format(
            parseLocal(values.orderInformation.responseDeadline),
            BACKEND_DATE_FORMAT
          )
        : format(values.orderInformation.responseDeadline, BACKEND_DATE_FORMAT);

    const body = {
      organizationCode: values.subcontractorInformation.organizationCode,
      email: values.subcontractorInformation.email,
      address: values.subcontractorInformation.address,
      phone: values.subcontractorInformation.phone,
      deliveryNotes: values.deliveryNotes,
      responseDeadline,
      notes: values.orderInformation.notes,
      includeOrderedServices: values.includeOrderedServices,
      attachmentIds: this.props.attachments.map(
        attachment => attachment.attachmentId
      )
    };

    const sendOrderFn = () => {
      sendOrder(orderId, body).then(response => {
        if (response.success) {
          getOrder(orderId).then(({ success }) => {
            if (success) {
              Toast.success(t('Order sent successfully'));
            }

            this.setState({
              preview: true
            });
            actions.setSubmitting(false);
          });
        } else {
          Toast.warning(response.payload.message);
          actions.setSubmitting(false);
        }
      });
    };

    const updateAndSendOrderFn = () => {
      updateAndSend(orderId, {
        ...body,
        serviceRequests: this.getServiceRequests().map(
          item => item.serviceRequestId
        )
      }).then(response => {
        if (response.success) {
          getOrder(orderId).then(({ success }) => {
            if (success) {
              Toast.success(t('Order sent successfully'));
              history.push('/orders');
            }

            this.setState({
              preview: true
            });

            actions.setSubmitting(false);
          });
        } else {
          Toast.warning(response.payload.message);
          actions.setSubmitting(false);
        }
      });
    };

    if (values.action_type === SUBMIT_TYPES.CANCEL_ORDER) {
      Confirm(
        t('Cancel order'),
        t('Are you sure you want to cancel this order?'),
        () => {
          cancelOrder(orderId, body).then(response => {
            if (response.success) {
              Toast.success('Order cancelled successfully');
              history.push('/orders');
            } else {
              Toast.warning(response.payload.message);
            }
          });
        },
        () => {
          actions.setSubmitting(false);
        }
      );
    }

    if (
      !values.action_type ||
      values.action_type === SUBMIT_TYPES.SAVE_ORDER ||
      values.action_type === SUBMIT_TYPES.UPDATE_AND_SEND
    ) {
      if (body.attachmentIds.length === 0) {
        Confirm(
          t('Send order'),
          t(
            'Are you sure, you want to send this order without any attachments?'
          ),
          () => {
            if (values.action_type === SUBMIT_TYPES.UPDATE_AND_SEND) {
              updateAndSendOrderFn();
            } else {
              sendOrderFn();
            }
          },
          () => {
            actions.setSubmitting(false);
          }
        );
      } else {
        if (values.action_type === SUBMIT_TYPES.UPDATE_AND_SEND) {
          updateAndSendOrderFn();
        } else {
          sendOrderFn();
        }
      }
    }

    if (values.action_type === SUBMIT_TYPES.SAVE_DRAFT) {
      updateOrder(orderId, body).then(response => {
        if (response.success) {
          Toast.success(t('Order draft saved successfully'));
          history.push('/orders');
        } else {
          Toast.warning(response.payload.message);
        }
        actions.setSubmitting(false);
      });
    }
  };

  onDownloadOrder = () => {
    const {
      downloadOrder,
      match: {
        params: { orderId }
      }
    } = this.props;

    downloadOrder(orderId).then(response => {
      Download(response, 'Order.xlsx');
    });
  };

  onDeleteOrderAssignment = id => {
    if (!this.isOrderDraft()) {
      this.deleteAssignmentFromOrder(id);
    } else {
      this.deleteAssignmentFromDraft(id);
    }
  };

  deleteAssignmentFromOrder = id => {
    const { t, canRemoveServiceRequest } = this.props;

    canRemoveServiceRequest(id).then(response => {
      if (!response.success) {
        Toast.warning(
          response.payload != null
            ? response.payload.message
            : 'An error occurred'
        );
      } else {
        Confirm(
          t('Service request'),
          t(
            'Are you sure you want to remove this service line from the order?'
          ),
          () => {
            const deletedServiceRequests = this.state.deletedServiceRequests;
            deletedServiceRequests.push(id);
            this.setState({
              deletedServiceRequests
            });
            this.forceUpdate();
          }
        );
      }
    });
  };

  deleteAssignmentFromDraft = id => {
    const {
      history,
      match: {
        params: { orderId }
      },
      t,
      deleteOrderAssignment
    } = this.props;

    Confirm(
      t('Service request'),
      t('Are you sure you want to remove this service line from the order?'),
      () => {
        deleteOrderAssignment(orderId, id).then(response => {
          if (!response.success) {
            Toast.warning(response.payload.message);
          } else {
            this.props.getOrder(orderId).then(({ success }) => {
              if (!success) {
                history.push('/orders');
              }
            });
          }
        });
      }
    );
  };

  onDeleteOrder = () => {
    const {
      t,
      deleteOrder,
      history,
      match: {
        params: { orderId }
      }
    } = this.props;

    Confirm(
      t('Delete order'),
      t('Are you sure you want to delete this order?'),
      () => {
        deleteOrder(orderId).then(response => {
          if (response.success) {
            history.push('/orders');
          } else {
            Toast.warning(response.payload.message);
          }
        });
      }
    );
  };

  onUpdateOrder = () => {
    const {
      match: {
        params: { orderId }
      },
      canUpdate
    } = this.props;

    canUpdate(orderId).then(response => {
      if (response.success) {
        this.setState({
          preview: false
        });
      } else if (response.payload && response.payload.message) {
        Toast.warning(response.payload.message);
      }
    });
  };

  onCancelOrderChanges = resetForm => {
    const { t } = this.props;

    Confirm(
      t('Cancel order update'),
      t('Are you sure you want to cancel order update?'),
      () => {
        this.setState(
          {
            preview: true,
            deletedServiceRequests: []
          },
          () => {
            resetForm();
            this.props.setAttachments(this.props.order.attachments);
          }
        );
      }
    );
  };

  getServiceRequests() {
    const {
      order: { serviceRequests }
    } = this.props;

    return serviceRequests.filter(
      item =>
        this.state.deletedServiceRequests.indexOf(item.serviceRequestId) === -1
    );
  }

  getOrderCurrency() {
    const { order } = this.props;

    if (!order.orderCurrency) {
      return ''
    }

    return order.orderCurrency.name;
  }

  isHostingNationParty() {
    const { currentParty } = this.props;
    
    if (!currentParty) {
      return false;
    }

    return currentParty.partyTypeId === PARTY_TYPE.HostNation.Id;
  }

  navigateBack = () => () => {
    const { history } = this.props;

    history.push('/orders');
  };

  render() {
    const { t, order, isLoading, history } = this.props;

    if (isLoading) {
      return null;
    }

    return (
      <>
        <Formik
          onSubmit={this.onSubmit}
          validateOnBlur={false}
          validationSchema={this.validationSchema}
          initialValues={this.getInitialValues()}
        >
          {({
            handleSubmit,
            isSubmitting,
            setFieldValue,
            values,
            dirty,
            resetForm
          }) => (
            <form onSubmit={handleSubmit} ref={this.form}>
              <PageActions container={this.form.current}>
                <div>
                  {this.isOrderDraft() && (
                    <GhostButton
                      large
                      danger
                      disabled={this.props.hasUserPermission(
                        USER_ROLES.READONLY
                      )}
                      onClick={this.onDeleteOrder}
                    >
                      {t('Delete draft')}
                    </GhostButton>
                  )}
                  {!this.isOrderDraft() && this.isPreviewMode() && (
                    <>
                      <GhostButton
                        large
                        danger
                        disabled={this.props.hasUserPermission(
                          USER_ROLES.READONLY
                        )}
                        onClick={async () => {
                          await setFieldValue(
                            'action_type',
                            SUBMIT_TYPES.CANCEL_ORDER
                          );
                          handleSubmit();
                        }}
                      >
                        {t('Cancel order')}
                      </GhostButton>
                      <GhostButton
                        disabled={this.props.hasUserPermission(
                          USER_ROLES.READONLY
                        )}
                        large
                        onClick={this.onUpdateOrder}
                      >
                        {t('Update order')}
                      </GhostButton>
                    </>
                  )}
                </div>
                <div className="order--page-actions-left">
                  {this.isPreviewMode() && (
                    <>
                      <GhostButton
                        large
                        onClick={this.onDownloadOrder}
                        iconLeft={<Icon icon="download" />}
                        type="button"
                      >
                        {t('Download order')}
                      </GhostButton>
                      <RaisedButton
                        large
                        type="button"
                        onClick={async () => {
                          await setFieldValue(
                            'action_type',
                            SUBMIT_TYPES.SAVE_ORDER
                          );
                          handleSubmit();
                        }}
                        disabled={
                          isSubmitting ||
                          this.props.hasUserPermission(USER_ROLES.READONLY)
                        }
                      >
                        {t('Resend order')}
                      </RaisedButton>
                    </>
                  )}
                  {!this.isPreviewMode() && (
                    <>
                      {this.isOrderDraft() && (
                        <GhostButton
                          large
                          onClick={async () => {
                            await setFieldValue(
                              'action_type',
                              SUBMIT_TYPES.SAVE_DRAFT
                            );
                            handleSubmit();
                          }}
                          disabled={isSubmitting || !dirty}
                          type="button"
                        >
                          {t('Save draft')}
                        </GhostButton>
                      )}

                      {!this.isOrderDraft() && (
                        <GhostButton
                          large
                          onClick={() => {
                            this.onCancelOrderChanges(resetForm);
                          }}
                          disabled={isSubmitting}
                          type="button"
                        >
                          {t('Cancel')}
                        </GhostButton>
                      )}

                      <RaisedButton
                        large
                        onClick={async () => {
                          await setFieldValue(
                            'action_type',
                            this.isOrderDraft()
                              ? SUBMIT_TYPES.SAVE_ORDER
                              : SUBMIT_TYPES.UPDATE_AND_SEND
                          );
                          handleSubmit();
                        }}
                        type="button"
                        disabled={isSubmitting}
                      >
                        {t('Send order')}
                      </RaisedButton>
                    </>
                  )}
                </div>
              </PageActions>
              <div className="order">
              <ServiceRequestNavContext.Consumer>
                {() => {
                  this.props.withEscNavigationCallback(
                    this.navigateBack()
                  );
                }}
              </ServiceRequestNavContext.Consumer>
                <PageHeader
                  to="/orders"
                  title={
                    (this.isPreviewMode()
                      ? t('Preview order')
                      : t('Edit order')) + ' -'
                  }
                  onClick={event => {
                    if (!this.isPreviewMode() && dirty) {
                      event.preventDefault();
                      Confirm(
                        t('Cancel order update'),
                        t('Are you sure you want to cancel order update?'),
                        () => {
                          history.push('/orders');
                        }
                      );
                    }
                  }}
                  order={order.orderInformation.number}
                  status={{
                    name: order.status.longName,
                    order: true,
                    id: order.status.order
                  }}
                />

                <div className="order--form">
                  <Panel showFootnote={false}>
                    <div className="order--panel">
                      <H2>{t('Subcontractor information')}</H2>

                      <Field type="hidden" name="hash" />

                      <div className="order--field">
                        <InputLabel>{t('Org. Name')}</InputLabel>
                        <div>
                          {order.subcontractorInformation.organizationName}
                        </div>
                      </div>

                      {!this.isPreviewMode() && (
                        <div className="order--field">
                          <Field
                            component={Input}
                            name="subcontractorInformation.organizationCode"
                          >
                            {t('Org. Code')}
                          </Field>
                        </div>
                      )}

                      {this.isPreviewMode() && (
                        <div className="order--field">
                          <InputLabel>{t('Org. Code')}</InputLabel>
                          <div>
                            {order.subcontractorInformation.organizationCode}
                          </div>
                        </div>
                      )}

                      {!this.isPreviewMode() && (
                        <Field
                          component={Input}
                          disabled={this.props.hasUserPermission(
                            USER_ROLES.READONLY
                          )}
                          name="subcontractorInformation.email"
                        >
                          {t('Email')}
                        </Field>
                      )}

                      {this.isPreviewMode() && (
                        <div className="order--field">
                          <Field
                            component={Input}
                            noMargin
                            disabled={this.props.hasUserPermission(
                              USER_ROLES.READONLY
                            )}
                            name="subcontractorInformation.email"
                          >
                            {t('Email')}
                          </Field>
                        </div>
                      )}

                      {!this.isPreviewMode() && (
                        <>
                          <Field
                            component={Input}
                            name="subcontractorInformation.address"
                          >
                            {t('Address')}
                          </Field>
                          <Field
                            component={Input}
                            name="subcontractorInformation.phone"
                          >
                            {t('Phone')}
                          </Field>
                        </>
                      )}

                      {this.isPreviewMode() && (
                        <>
                          <div className="order--field">
                            <InputLabel>{t('Address')}</InputLabel>
                            <div>{order.subcontractorInformation.address}</div>
                          </div>
                          <div className="order--field">
                            <InputLabel>{t('Phone')}</InputLabel>
                            <div>{order.subcontractorInformation.phone}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </Panel>
                  <div className="order--middle">
                    <Panel showFootnote={false}>
                      <div className="order--panel">
                        <H2>{t('Delivery information')}</H2>

                        {!this.isPreviewMode() && (
                          <div className="order--field">
                            <Field
                              component={Textarea}
                              name="deliveryNotes"
                              placeholder={t('Add notes')}
                              label={t('Notes')}
                            />
                          </div>
                        )}
                        {this.isPreviewMode() && (
                          <div className="order--field">
                            <InputLabel>{t('Notes')}</InputLabel>
                            <div>{this.getFieldValue(order.deliveryNotes)}</div>
                          </div>
                        )}
                      </div>
                    </Panel>
                    <Panel showFootnote={false}>
                      <div className="order--panel">
                        <H2>{t('Attachments')}</H2>

                        {this.isPreviewStateMode() && (
                          <div className="order--attachments">
                            {this.props.attachments.map(file => (
                              <File
                                key={file.attachmentId}
                                name={file.name}
                                attachmentId={file.attachmentId}
                              />
                            ))}
                          </div>
                        )}

                        {!this.isPreviewStateMode() && (
                          <DropZone
                            disabled={this.props.hasUserPermission(
                              USER_ROLES.READONLY
                            )}
                            multiple={true}
                            value={this.props.attachments}
                            onSelect={files => {
                              setFieldValue('hash', ShortId.generate());
                              this.handleFileUpload(files);
                            }}
                            onRemove={file => {
                              setFieldValue('hash', ShortId.generate());
                              this.props.deleteAttachment(file);
                            }}
                          />
                        )}

                        <div className="order--download-settings">
                          <Field
                            disabled={this.isPreviewMode()}
                            component={Switch}
                            setFieldValue={setFieldValue}
                            key={values.includeOrderedServices}
                            name="includeOrderedServices"
                            defaultChecked={values.includeOrderedServices}
                            wrapperClassName="order--download-settings-switch"
                          />
                          <span>{t('Include xlsx with ordered services')}</span>
                        </div>
                      </div>
                    </Panel>
                  </div>
                  <Panel showFootnote={false}>
                    <div className="order--panel">
                      <H2>{t('Order information')}</H2>

                      <div className="order--field">
                        <InputLabel>{t('Number')}</InputLabel>
                        <div>{order.orderInformation.number}</div>
                      </div>

                      <div className="order--panel-line">
                        <div className="order--field">
                          <InputLabel>{t('Date')}</InputLabel>
                          <div>
                            {format(
                              order.orderInformation.creationDate,
                              DATE_FORMAT
                            )}
                          </div>
                        </div>

                        {!this.isPreviewMode() && (
                          <div className="order--field">
                            <Field
                              name="orderInformation.responseDeadline"
                              noMargin
                              minDate={
                                new Date(order.orderInformation.creationDate)
                              }
                              setFieldValue={setFieldValue}
                              component={DateInput}
                            >
                              {t('Response deadline')}
                            </Field>
                          </div>
                        )}

                        {this.isPreviewMode() && (
                          <div className="order--field">
                            <InputLabel>{t('Response deadline')}</InputLabel>
                            <div>
                              {format(
                                order.orderInformation.responseDeadline,
                                DATE_FORMAT
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="order--field">
                        <InputLabel>
                          {t(`Total cost`)}
                          {this.isHostingNationParty() && ` (${this.getOrderCurrency()})`}
                          *
                        </InputLabel>
                        <div>
                          <Money amount={order.orderInformation.totalCost} />
                        </div>
                      </div>

                      {!this.isPreviewMode() && (
                        <div className="order--field">
                          <Field
                            name="orderInformation.notes"
                            label={t('Notes')}
                            placeholder={t('Add notes')}
                            component={Textarea}
                          ></Field>
                        </div>
                      )}

                      {this.isPreviewMode() && (
                        <div className="order--field">
                          <InputLabel>{t('Notes')}</InputLabel>
                          <div>
                            {this.getFieldValue(order.orderInformation.notes)}
                          </div>
                        </div>
                      )}
                    </div>
                  </Panel>
                </div>

                {this.getServiceRequests().length > 0 && (
                  <div>
                    <H2>{t('Service requests')}</H2>

                    <table className="table">
                      <tbody>
                        <tr>
                          <th>{t('Request number')}</th>
                          <th>{t('Code')}</th>
                          <th>{t('Name')}</th>
                          <th>{t('Quantity')}</th>
                          {this.isHostingNationParty() && <th>{t('Currency')}</th>}
                          <th>{t('Unit price*')}</th>
                          <th>{t('Total price*')}</th>
                          <th>{t('Comment')}</th>
                          {!this.isPreviewMode() && <th />}
                        </tr>

                        {this.getServiceRequests().map(item => {
                          return (
                            <tr key={item.serviceRequestId}>
                              <td>{item.requestNumber}</td>
                              <td>{item.code}</td>
                              <td>{item.name}</td>
                              <td>
                                <Quantity amount={item.quantity} />
                              </td>
                              {this.isHostingNationParty() && <td>{this.getOrderCurrency()}</td>}
                              <td>
                                <Money amount={item.unitPrice} />
                              </td>
                              <td>
                                <Money amount={item.totalPrice} />
                              </td>
                              <td className="last">
                                {this.getFieldValue(item.comment)}
                              </td>
                              {!this.isPreviewMode() && (
                                <td className="empty order--remove-button">
                                  <GhostButton
                                    onClick={() => {
                                      this.onDeleteOrderAssignment(
                                        item.serviceRequestId
                                      );
                                    }}
                                  >
                                    <Icon icon="delete" />
                                  </GhostButton>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </form>
          )}
        </Formik>
      </>
    );
  }
}

Order.propTypes = {
  t: PropTypes.func,
  isLoading: PropTypes.bool,
  match: PropTypes.object,
  history: PropTypes.object,
  attachments: PropTypes.array,
  order: PropTypes.object,
  getOrder: PropTypes.func,
  uploadAttachments: PropTypes.func,
  cancelOrder: PropTypes.func,
  downloadOrder: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  updateOrder: PropTypes.func,
  deleteOrderAssignment: PropTypes.func,
  hasUserPermission: PropTypes.func,
  sendOrder: PropTypes.func,
  deleteOrder: PropTypes.func,
  canUpdate: PropTypes.func,
  updateAndSend: PropTypes.func,
  canRemoveServiceRequest: PropTypes.func,
  getCurrentParty: PropTypes.func,
  currentParty: PropTypes.object,
  withEscNavigationCallback: PropTypes.func
};

export default WithEscNavigation(Order);
